import { useState } from 'react';
import {
  ArrowDown,
  ArrowDownLeft,
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpLeft,
  ArrowUpRight,
  Copy,
  CornerUpLeft,
  Locate,
  Maximize2,
  RotateCcw,
  RotateCw,
  Trash2,
} from 'lucide-react';
import type {
  Canto,
  ConfigCamadaImagem,
  Formato,
  Lado,
  ModoRodizio,
  PapelImagem,
} from '@miolos/core';
import {
  CANTOS,
  LADOS,
  MODOS_RODIZIO,
  PAPEIS_IMAGEM,
  PASSOS_POSICAO_MM,
  ROTULO_CANTO,
  ROTULO_ESCOPO,
  ROTULO_LADO,
  ROTULO_PAPEL_IMAGEM,
  ROTULO_RODIZIO,
  SIMBOLO_CANTO,
  centralizarCamada,
  encaixarNaBorda,
  medirPagina,
  moverCamada,
  preencherPagina,
  redimensionarCamada,
} from '@miolos/core';
import type { AjusteImagem, EscopoImagem } from '@miolos/core';
import { AJUSTES_IMAGEM, ESCOPOS_IMAGEM, ROTULO_AJUSTE } from '@miolos/core';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface Props {
  camada: ConfigCamadaImagem;
  formato: Formato;
  aoAtualizar: (alteracoes: Partial<ConfigCamadaImagem>) => void;
  aoReplicar: (tipo: 'CANTOS' | 'BORDAS') => void;
  aoDuplicar: () => void;
  aoRemover: () => void;
  aoTrazerParaFrente: () => void;
}

function Seccao({ titulo, children, dica }: { titulo: string; children: React.ReactNode; dica?: string }) {
  return (
    <section className="space-y-2.5 rounded-lg border border-border bg-card p-3">
      <div className="space-y-0.5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</h4>
        {dica ? <p className="text-xs leading-relaxed text-muted-foreground">{dica}</p> : null}
      </div>
      {children}
    </section>
  );
}

function BotaoIcone({
  rotulo,
  aoClicar,
  children,
  className,
}: {
  rotulo: string;
  aoClicar: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Button
      type="button"
      variant="contorno"
      tamanho="icone"
      title={rotulo}
      aria-label={rotulo}
      onClick={aoClicar}
      className={cn('size-8', className)}
    >
      {children}
    </Button>
  );
}

function BotaoPasso({
  rotulo,
  aoClicar,
  children,
}: {
  rotulo: string;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button type="button" variant="contorno" tamanho="pequeno" onClick={aoClicar} className="h-7 px-2">
      {children ?? rotulo}
    </Button>
  );
}

export function CamadaEditor({
  camada,
  formato,
  aoAtualizar,
  aoReplicar,
  aoDuplicar,
  aoRemover,
  aoTrazerParaFrente,
}: Props) {
  const [passo, setPasso] = useState<number>(1);
  const medidas = medirPagina(formato);

  const mover = (direcaoX: number, direcaoY: number) =>
    aoAtualizar(moverCamada(camada, direcaoX * passo, direcaoY * passo));

  const papelPermiteEspelhar = camada.papel !== 'PAGINA_INTEIRA';

  return (
    <div className="space-y-3">
      {/* Tipo de encaixe */}
      <Seccao
        titulo="Como a imagem se encaixa"
        dica="Muda a forma de ancorar: página toda, faixa colada numa borda, canto ou posição livre."
      >
        <div className="grid grid-cols-2 gap-2">
          {PAPEIS_IMAGEM.map((papel: PapelImagem) => (
            <button
              key={papel}
              type="button"
              onClick={() => aoAtualizar({ papel })}
              aria-pressed={camada.papel === papel}
              className={cn(
                'rounded-md border px-2.5 py-2 text-xs font-medium transition-colors',
                camada.papel === papel
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-card hover:border-primary/40',
              )}
            >
              {ROTULO_PAPEL_IMAGEM[papel]}
            </button>
          ))}
        </div>

        {camada.papel === 'BORDA' ? (
          <Campo rotulo="Qual borda">
            <div className="grid grid-cols-4 gap-2">
              {LADOS.map((lado: Lado) => (
                <button
                  key={lado}
                  type="button"
                  onClick={() => aoAtualizar(encaixarNaBorda(camada, medidas, lado))}
                  aria-pressed={camada.lado === lado}
                  className={cn(
                    'rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
                    camada.lado === lado
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card hover:border-primary/40',
                  )}
                >
                  {ROTULO_LADO[lado]}
                </button>
              ))}
            </div>
          </Campo>
        ) : null}

        {camada.papel === 'CANTO' ? (
          <Campo rotulo="Qual canto">
            <div className="grid grid-cols-4 gap-2">
              {CANTOS.map((canto: Canto) => (
                <button
                  key={canto}
                  type="button"
                  onClick={() => aoAtualizar({ canto })}
                  aria-pressed={camada.canto === canto}
                  title={ROTULO_CANTO[canto]}
                  className={cn(
                    'rounded-md border py-1.5 text-base leading-none transition-colors',
                    camada.canto === canto
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card hover:border-primary/40',
                  )}
                >
                  {SIMBOLO_CANTO[canto]}
                </button>
              ))}
            </div>
          </Campo>
        ) : null}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            variant="contorno"
            tamanho="pequeno"
            onClick={() => aoAtualizar(preencherPagina(camada, medidas))}
          >
            <Maximize2 />
            Página inteira
          </Button>
          <Button
            type="button"
            variant="contorno"
            tamanho="pequeno"
            onClick={() => aoAtualizar(centralizarCamada(camada, medidas, 'HORIZONTAL'))}
          >
            Centralizar na horizontal
          </Button>
          <Button
            type="button"
            variant="contorno"
            tamanho="pequeno"
            onClick={() => aoAtualizar(centralizarCamada(camada, medidas, 'VERTICAL'))}
          >
            Centralizar na vertical
          </Button>
          <Button
            type="button"
            variant="contorno"
            tamanho="pequeno"
            onClick={() => aoAtualizar(centralizarCamada(camada, medidas, 'AMBOS'))}
          >
            <Locate />
            Centro da página
          </Button>
        </div>
      </Seccao>

      {/* Posicionamento com botões */}
      <Seccao
        titulo="Posição"
        dica="Cada clique move a imagem na direção da seta. As setas respeitam o lado em que a imagem está ancorada."
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">Passo de cada clique</span>
          <div className="flex gap-1.5">
            {PASSOS_POSICAO_MM.map((valor) => (
              <button
                key={valor}
                type="button"
                onClick={() => setPasso(valor)}
                aria-pressed={passo === valor}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                  passo === valor
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card hover:border-primary/40',
                )}
              >
                {valor} mm
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="grid grid-cols-3 gap-1.5">
            <BotaoIcone rotulo="Mover para cima e para a esquerda" aoClicar={() => mover(-1, -1)}>
              <ArrowUpLeft />
            </BotaoIcone>
            <BotaoIcone rotulo="Mover para cima" aoClicar={() => mover(0, -1)}>
              <ArrowUp />
            </BotaoIcone>
            <BotaoIcone rotulo="Mover para cima e para a direita" aoClicar={() => mover(1, -1)}>
              <ArrowUpRight />
            </BotaoIcone>

            <BotaoIcone rotulo="Mover para a esquerda" aoClicar={() => mover(-1, 0)}>
              <ArrowLeft />
            </BotaoIcone>
            <BotaoIcone
              rotulo="Centralizar na página"
              aoClicar={() => aoAtualizar(centralizarCamada(camada, medidas, 'AMBOS'))}
            >
              <Locate />
            </BotaoIcone>
            <BotaoIcone rotulo="Mover para a direita" aoClicar={() => mover(1, 0)}>
              <ArrowRight />
            </BotaoIcone>

            <BotaoIcone rotulo="Mover para baixo e para a esquerda" aoClicar={() => mover(-1, 1)}>
              <ArrowDownLeft />
            </BotaoIcone>
            <BotaoIcone rotulo="Mover para baixo" aoClicar={() => mover(0, 1)}>
              <ArrowDown />
            </BotaoIcone>
            <BotaoIcone rotulo="Mover para baixo e para a direita" aoClicar={() => mover(1, 1)}>
              <ArrowDownRight />
            </BotaoIcone>
          </div>

          <dl className="grid flex-1 grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">X</dt>
            <dd className="font-mono">{camada.xMm} mm</dd>
            <dt className="text-muted-foreground">Y</dt>
            <dd className="font-mono">{camada.yMm} mm</dd>
            <dt className="text-muted-foreground">Página</dt>
            <dd className="font-mono">
              {medidas.larguraMm} × {medidas.alturaMm}
            </dd>
            <dt className="text-muted-foreground">Imagem</dt>
            <dd className="font-mono">
              {camada.larguraMm} × {Math.round(camada.alturaMm * 10) / 10}
            </dd>
          </dl>
        </div>
      </Seccao>

      {/* Tamanho */}
      <Seccao titulo="Tamanho">
        <LinhaSwitch
          id={`prop-${camada.id}`}
          rotulo="Manter proporção da imagem"
          descricao="A altura acompanha a largura, sem distorcer a figura."
          checked={camada.proporcional}
          onCheckedChange={(valor) => {
            const proporcao = camada.proporcao ?? 1;
            aoAtualizar({
              proporcional: valor,
              alturaMm: valor ? Math.round((camada.larguraMm / proporcao) * 10) / 10 : camada.alturaMm,
            });
          }}
        />

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-16 text-xs text-muted-foreground">Largura</span>
            <BotaoPasso rotulo="-5 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, -5, 0))}>
              −5
            </BotaoPasso>
            <BotaoPasso rotulo="-1 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, -1, 0))}>
              −1
            </BotaoPasso>
            <span className="w-16 text-center font-mono text-xs">{Math.round(camada.larguraMm)} mm</span>
            <BotaoPasso rotulo="+1 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, 1, 0))}>
              +1
            </BotaoPasso>
            <BotaoPasso rotulo="+5 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, 5, 0))}>
              +5
            </BotaoPasso>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="w-16 text-xs text-muted-foreground">Altura</span>
            <BotaoPasso rotulo="-5 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, 0, -5))}>
              −5
            </BotaoPasso>
            <BotaoPasso rotulo="-1 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, 0, -1))}>
              −1
            </BotaoPasso>
            <span className="w-16 text-center font-mono text-xs">
              {Math.round(camada.proporcional ? camada.larguraMm / (camada.proporcao ?? 1) : camada.alturaMm)} mm
            </span>
            <BotaoPasso rotulo="+1 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, 0, 1))}>
              +1
            </BotaoPasso>
            <BotaoPasso rotulo="+5 mm" aoClicar={() => aoAtualizar(redimensionarCamada(camada, 0, 5))}>
              +5
            </BotaoPasso>
          </div>

          {camada.proporcional ? (
            <p className="text-xs text-muted-foreground">
              A altura é calculada pela proporção original da imagem.
            </p>
          ) : null}
        </div>
      </Seccao>

      {/* Rotação */}
      <Seccao titulo="Rotação">
        <div className="flex flex-wrap items-center gap-2">
          <BotaoPasso rotulo="Girar 90° no sentido anti-horário" aoClicar={() => aoAtualizar({ rotacaoGraus: camada.rotacaoGraus - 90 })}>
            <RotateCcw /> 90°
          </BotaoPasso>
          <BotaoPasso rotulo="Girar 5° no sentido anti-horário" aoClicar={() => aoAtualizar({ rotacaoGraus: camada.rotacaoGraus - 5 })}>
            −5°
          </BotaoPasso>
          <BotaoPasso rotulo="Girar 1° no sentido anti-horário" aoClicar={() => aoAtualizar({ rotacaoGraus: camada.rotacaoGraus - 1 })}>
            −1°
          </BotaoPasso>
          <span className="w-14 text-center font-mono text-xs">{camada.rotacaoGraus}°</span>
          <BotaoPasso rotulo="Girar 1° no sentido horário" aoClicar={() => aoAtualizar({ rotacaoGraus: camada.rotacaoGraus + 1 })}>
            +1°
          </BotaoPasso>
          <BotaoPasso rotulo="Girar 5° no sentido horário" aoClicar={() => aoAtualizar({ rotacaoGraus: camada.rotacaoGraus + 5 })}>
            +5°
          </BotaoPasso>
          <BotaoPasso rotulo="Girar 90° no sentido horário" aoClicar={() => aoAtualizar({ rotacaoGraus: camada.rotacaoGraus + 90 })}>
            <RotateCw /> 90°
          </BotaoPasso>
          <BotaoPasso rotulo="Zerar rotação" aoClicar={() => aoAtualizar({ rotacaoGraus: 0 })}>
            Zerar
          </BotaoPasso>
        </div>
      </Seccao>

      {/* Aparência */}
      <Seccao titulo="Aparência" dica="O véu clareia a imagem para o texto continuar legível na impressão.">
        {(
          [
            { chave: 'opacidade', rotulo: 'Opacidade', min: 0.02, max: 1, passo: 0.01, fator: 100, sufixo: '%' },
            { chave: 'veu', rotulo: 'Véu de leitura', min: 0, max: 0.95, passo: 0.01, fator: 100, sufixo: '%' },
            { chave: 'desfoque', rotulo: 'Desfoque', min: 0, max: 16, passo: 1, fator: 1, sufixo: ' px' },
          ] as const
        ).map((controle) => (
          <div key={controle.chave} className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{controle.rotulo}</span>
              <span className="font-mono text-muted-foreground">
                {Math.round(camada[controle.chave] * controle.fator)}
                {controle.sufixo}
              </span>
            </div>
            <Slider
              value={[camada[controle.chave]]}
              min={controle.min}
              max={controle.max}
              step={controle.passo}
              onValueChange={([valor]) => aoAtualizar({ [controle.chave]: valor ?? camada[controle.chave] })}
            />
          </div>
        ))}

        <Campo rotulo="Enquadramento dentro da moldura">
          <Select
            value={camada.ajuste}
            onValueChange={(valor) => aoAtualizar({ ajuste: valor as AjusteImagem })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AJUSTES_IMAGEM.map((ajuste) => (
                <SelectItem key={ajuste} value={ajuste}>
                  {ROTULO_AJUSTE[ajuste]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>
      </Seccao>

      {/* Distribuição entre páginas */}
      <Seccao
        titulo="Em quais páginas aparece"
        dica="Combine com outras camadas do mesmo tipo para criar rodízios."
      >
        <Campo rotulo="Escopo">
          <Select
            value={camada.escopo}
            onValueChange={(valor) => aoAtualizar({ escopo: valor as EscopoImagem })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ESCOPOS_IMAGEM.map((escopo) => (
                <SelectItem key={escopo} value={escopo}>
                  {ROTULO_ESCOPO[escopo]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo rotulo="Distribuição">
          <Select
            value={camada.rodizio}
            onValueChange={(valor) => aoAtualizar({ rodizio: valor as ModoRodizio })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODOS_RODIZIO.map((modo) => (
                <SelectItem key={modo} value={modo}>
                  {ROTULO_RODIZIO[modo]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        {camada.rodizio === 'A_CADA_N' ? (
          <Campo
            rotulo="Páginas seguidas por camada"
            dica="Ex.: 10 com 10 camadas dá um bloco diferente a cada 10 páginas."
          >
            <Input
              type="number"
              min={1}
              max={400}
              value={camada.aCadaNPaginas}
              onChange={(evento) =>
                aoAtualizar({ aCadaNPaginas: Math.max(1, Number(evento.target.value) || 1) })
              }
              className="w-28"
            />
          </Campo>
        ) : null}

        {camada.rodizio === 'PAGINAS' ? (
          <Campo rotulo="Páginas" dica="Use vírgulas e intervalos, contando a partir de 1. Ex.: 1,3,5-10">
            <Input
              value={camada.paginas}
              onChange={(evento) => aoAtualizar({ paginas: evento.target.value })}
              placeholder="1,3,5-10"
              className="font-mono text-xs"
            />
          </Campo>
        ) : null}

        {papelPermiteEspelhar ? (
          <LinhaSwitch
            id={`espelho-${camada.id}`}
            rotulo="Espelhar nas páginas pares"
            descricao="Inverte a posição horizontal no verso, seguindo a lombada."
            checked={camada.espelharPares}
            onCheckedChange={(valor) => aoAtualizar({ espelharPares: valor })}
          />
        ) : null}
      </Seccao>

      {/* Replicação */}
      <Seccao
        titulo="Replicar esta imagem"
        dica="Cria cópias iguais nos outros cantos ou nas outras bordas — útil para molduras."
      >
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="contorno" tamanho="pequeno" onClick={() => aoReplicar('CANTOS')}>
            <CornerUpLeft />
            Nos 4 cantos
          </Button>
          <Button type="button" variant="contorno" tamanho="pequeno" onClick={() => aoReplicar('BORDAS')}>
            <Maximize2 />
            Nas 4 bordas
          </Button>
          <Button type="button" variant="contorno" tamanho="pequeno" onClick={aoDuplicar}>
            <Copy />
            Duplicar camada
          </Button>
          <Button type="button" variant="fantasma" tamanho="pequeno" onClick={aoTrazerParaFrente}>
            Trazer para frente
          </Button>
          <Button type="button" variant="fantasma" tamanho="pequeno" onClick={aoRemover}>
            <Trash2 />
            Excluir camada
          </Button>
        </div>
      </Seccao>
    </div>
  );
}
